/**
 * Y GO Shuttle Services France - Homepage / Vitrine
 * LOT 9 - Page d'accueil publique optimisée SEO
 * Premium VTC Transport Platform - avec i18n et CMS dynamique
 */
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Car, 
  Plane, 
  Ship, 
  Clock, 
  MapPin, 
  Phone, 
  Mail, 
  Check, 
  Star,
  Wifi,
  Droplets,
  Baby,
  ChevronRight,
  Calendar,
  Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Header, Footer } from '../components/layout';
import { useCMSPage, getCMSValue, getCMSSection } from '../hooks/useCMSPage';
import SafeImage from '../components/SafeImage';
import ElfsightReviewsSection from '../components/ElfsightReviewsSection';
import HomeCtaSection from '../components/booking/HomeCtaSection';

// Default Image URLs from stock photos (fallback)
const DEFAULT_IMAGES = {
  hero: 'https://images.unsplash.com/photo-1764090317623-06968349ad23?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBibGFjayUyME1lcmNlZGVzJTIwc2VkYW4lMjBjaGF1ZmZldXIlMjBWVEN8ZW58MHx8fHwxNzY4OTM2MTc3fDA&ixlib=rb-4.1.0&q=85',
  etretat: 'https://images.unsplash.com/flagged/photo-1569750928069-a9e87dacd575?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwzfHxFdHJldGF0JTIwY2xpZmZzJTIwTm9ybWFuZHklMjBGcmFuY2UlMjBjb2FzdHxlbnwwfHx8fDE3Njg5MzYxNzl8MA&ixlib=rb-4.1.0&q=85',
  honfleur: 'https://images.unsplash.com/photo-1704367630537-8d0dbc0d67ec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxIb25mbGV1ciUyMGhhcmJvciUyME5vcm1hbmR5JTIwRnJhbmNlfGVufDB8fHx8MTc2ODkzNjE4MXww&ixlib=rb-4.1.0&q=85',
  deauville: 'https://images.unsplash.com/photo-1675774196611-9a342a727e5f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxEZWF1dmlsbGUlMjBiZWFjaCUyMEZyYW5jZSUyMGx1eHVyeXxlbnwwfHx8fDE3Njg5MzYxODR8MA&ixlib=rb-4.1.0&q=85',
  montSaintMichel: 'https://images.unsplash.com/photo-1505857807163-e4c177d2c3f1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxNb250JTIwU2FpbnQlMjBNaWNoZWwlMjBGcmFuY2V8ZW58MHx8fHwxNzY4OTM2MTg4fDA&ixlib=rb-4.1.0&q=85',
  dday: 'https://images.unsplash.com/photo-1683375805925-4a896ce6e2f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxELURheSUyMGJlYWNoZXMlMjBOb3JtYW5keSUyMG1lbW9yaWFsJTIwRnJhbmNlfGVufDB8fHx8MTc2ODkzNjIwMXww&ixlib=rb-4.1.0&q=85',
};

// Fleet vehicles (could be moved to CMS later)
const FLEET = [
  { name: 'Mercedes Classe E', type: 'Berline Premium', passengers: '3 passagers' },
  { name: 'Mercedes Classe V', type: 'Van Luxe', passengers: '7 passagers' },
  { name: 'Tesla Model Y', type: 'SUV Électrique', passengers: '4 passagers' },
  { name: 'VW Caravelle', type: 'Minibus', passengers: '8 passagers' },
];

// Default Excursions (fallback)
const DEFAULT_EXCURSIONS = [
  { id: 'etretat', title: 'Étretat & falaises', image: DEFAULT_IMAGES.etretat, duration: 'Demi-journée', description: 'Découvrez les célèbres falaises de craie et l\'Aiguille d\'Étretat.' },
  { id: 'honfleur', title: 'Honfleur & Côte Fleurie', image: DEFAULT_IMAGES.honfleur, duration: 'Demi-journée', description: 'Le pittoresque Vieux Bassin et les ruelles historiques.' },
  { id: 'deauville', title: 'Deauville / Trouville', image: DEFAULT_IMAGES.deauville, duration: 'Journée', description: 'Les planches de Deauville et l\'élégance de la Côte Fleurie.' },
  { id: 'dday', title: 'Plages du Débarquement', image: DEFAULT_IMAGES.dday, duration: 'Journée complète', description: 'Circuit historique DDAY : Omaha Beach, Pointe du Hoc, cimetière américain.' },
  { id: 'mont-saint-michel', title: 'Mont-Saint-Michel', image: DEFAULT_IMAGES.montSaintMichel, duration: 'Sur demande', description: 'Merveille de l\'Occident, accessible sur demande avec devis personnalisé.' },
];

// Hero Section - Dynamic with CMS
const HeroSection = ({ cmsContent }) => {
  const { t } = useTranslation();
  
  // Get banner data from CMS or use defaults
  const banner = cmsContent?.banner || {};
  // Hero — SafeImage handles fallback if the URL fails (Uploadcare/GCS expired)
  const heroImage = banner.image_url || DEFAULT_IMAGES.hero;
  const heroTitle = banner.title || t('home.heroTitle');
  const heroSubtitle = banner.subtitle || t('home.heroSubtitle');
  const heroDescription = banner.description || t('home.heroDescription');
  const ctaText = banner.cta_text || t('common.bookDriver');
  const ctaLink = banner.cta_link || '/booking';
  const overlayOpacity = banner.overlay_opacity || 60;
  
  return (
    <section 
      data-testid="hero-section" 
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <SafeImage
          src={heroImage}
          fallbackSrc={DEFAULT_IMAGES.hero}
          alt={heroTitle}
          className="w-full h-full object-cover"
        />
        {/* Premium dark gold overlay — no blur */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(90deg, rgba(11,11,11,0.9) 0%, rgba(11,11,11,0.7) 40%, rgba(11,11,11,0) 80%)'
          }}
        />
      </div>
      
      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="max-w-3xl space-y-6">
          {/* Ligne d'accroche (utilise le sous-titre CMS si présent) */}
          <p className="text-[10px] font-medium tracking-[0.35em] uppercase text-[#C9A24D]">
            {heroSubtitle || 'Chauffeur privé • Le Havre & Normandie'}
          </p>

          <h1
            className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-bold text-[#E5E5E5] leading-tight"
            style={{ textShadow: '0px 2px 10px rgba(0,0,0,0.5)' }}
          >
            {heroTitle}
          </h1>
          
          <p className="text-[#E5E5E5]/80 text-base sm:text-lg leading-relaxed max-w-2xl">
            {heroDescription}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 pt-2">
            <Link to={ctaLink}>
              <Button 
                data-testid="hero-cta-booking"
                className="bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold px-8 py-4 text-base rounded-none w-full sm:w-auto"
              >
                <Calendar className="w-5 h-5 mr-2" />
                {ctaText}
              </Button>
            </Link>
            
            <a href="tel:+33278780404">
              <Button 
                data-testid="hero-cta-phone"
                variant="outline" 
                className="border border-[#C9A24D]/60 text-[#C9A24D] hover:bg-[#C9A24D]/15 hover:border-[#C9A24D] px-8 py-4 text-base rounded-none w-full sm:w-auto transition-all duration-200"
              >
                <Phone className="w-5 h-5 mr-2" />
                +33 2 78 78 04 04
              </Button>
            </a>
          </div>

          {/* Points forts sous le hero */}
          <div className="flex flex-wrap gap-4 pt-4 text-sm text-[#E5E5E5]/80">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-[#C9A24D]" />
              <span>Transferts aéroports & gares</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-[#C9A24D]" />
              <span>Excursions privées en Normandie</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-[#C9A24D]" />
              <span>Services dédiés aux entreprises</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-[#C9A24D]/50 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-[#C9A24D] rounded-full mt-2 animate-pulse" />
        </div>
      </div>
    </section>
  );
};

// Zone d'intervention (SEO Local)
const ZoneSection = () => (
  <section data-testid="zone-section" className="py-20 bg-[#050509] border-t border-[#C9A24D]/10">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl grid lg:grid-cols-2 gap-10 items-start">
      <div>
        <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#F5F5F5] mb-4">
          Transport VTC au Havre et en <span className="text-[#C9A24D]">Normandie</span>
        </h2>
        <p className="text-[#A7A7A7] text-base sm:text-lg leading-relaxed mb-6">
          Y GO opère principalement au Havre et dans sa région, avec des transferts réguliers vers
          <span className="text-[#C9A24D]"> Honfleur</span>,
          <span className="text-[#C9A24D]"> Deauville</span>,
          <span className="text-[#C9A24D]"> Rouen</span>,
          <span className="text-[#C9A24D]"> Caen</span>,
          <span className="text-[#C9A24D]"> Étretat</span>,
          les <span className="text-[#C9A24D]">plages du Débarquement</span>, ainsi que les aéroports parisiens
          (<span className="text-[#C9A24D]">CDG, Orly, Beauvais</span>).
        </p>
        <div className="inline-flex items-center gap-3 bg-[#111111] px-6 py-3">
          <Clock className="w-5 h-5 text-[#C9A24D]" />
          <span className="text-[#F5F5F5] font-medium">Service disponible 24h/24 – 7j/7</span>
        </div>
      </div>
      <div className="bg-[#111111] border border-[#C9A24D]/18 p-6">
        <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#F5F5F5] mb-4">
          Principales zones desservies
        </h3>
        <p className="text-[#A7A7A7] text-sm mb-4">
          Prise en charge depuis le centre-ville du Havre, le terminal croisières, les hôtels et gares de la région.
        </p>
        <div className="grid grid-cols-2 gap-3 text-sm text-[#D4D4D4]">
          {['Le Havre', 'Honfleur', 'Deauville', 'Rouen', 'Caen', 'Paris CDG'].map((city) => (
            <div key={city} className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#C9A24D]" />
              <span>{city}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// Services Section - Dynamic with CMS
const ServicesSection = ({ cmsContent }) => {
  const { t } = useTranslation();
  
  // Get section data from CMS
  const section = getCMSSection(cmsContent, 'services');
  const sectionTitle = section?.title || t('home.ourServices');
  const sectionSubtitle = section?.subtitle || t('home.servicesSubtitle');
  
  const services = [
    {
      id: 'business',
      icon: Car,
      title: t('services.businessTransport'),
      description: t('services.businessDesc'),
    },
    {
      id: 'airports',
      icon: Plane,
      title: t('services.airportTransfers'),
      description: t('services.airportDesc'),
    },
    {
      id: 'cruises',
      icon: Ship,
      title: t('services.cruiseExcursions'),
      description: t('services.cruiseDesc'),
    },
  ];
  
  return (
    <section data-testid="services-section" className="py-20 bg-[#050509] border-t border-[#C9A24D]/10">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#F5F5F5] mb-4">
            Nos <span className="text-[#C9A24D]">Prestations</span>
          </h2>
          <p className="text-[#A7A7A7] text-base">
            {sectionSubtitle}
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8">
          {services.map((service) => {
            const Icon = service.icon;
            const link =
              service.id === 'business'
                ? '/entreprises'
                : service.id === 'airports'
                ? '/transferts'
                : '/croisieres-excursions';
            return (
              <Link
                key={service.id}
                to={link}
                data-testid={`service-card-${service.id}`}
                className="group relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] hover:shadow-[0_22px_55px_rgba(0,0,0,0.85)] transition-all p-6 flex flex-col justify-between backdrop-blur-sm"
              >
                <div>
                  <div className="w-12 h-12 mb-5 flex items-center justify-center bg-[#C9A24D]/12">
                    <Icon className="w-6 h-6 text-[#C9A24D]" />
                  </div>
                  <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#F5F5F5] mb-3">
                    {service.title}
                  </h3>
                  <p className="text-sm text-[#A7A7A7] leading-relaxed">{service.description}</p>
                </div>
                <p className="mt-6 text-sm font-medium text-[#C9A24D] group-hover:text-[#E6C77A]">
                  Découvrir le service →
                </p>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Differentiators Section - Dynamic with CMS
const DifferentiatorsSection = ({ cmsContent }) => {
  const { t } = useTranslation();
  
  // Get section data from CMS
  const section = getCMSSection(cmsContent, 'features');
  const sectionTitle = section?.title || t('home.whyChooseUs');
  
  // Parse differentiators from CMS content or use defaults
  let differentiators;
  if (section?.content) {
    // Try to parse list items from HTML content
    const matches = section.content.match(/<li>([^<]+)<\/li>/g);
    if (matches && matches.length > 0) {
      differentiators = matches.map(m => m.replace(/<\/?li>/g, ''));
    }
  }
  
  if (!differentiators || differentiators.length === 0) {
    differentiators = [
      t('differentiators.punctuality'),
      t('differentiators.flightTracking'),
      t('differentiators.meetGreet'),
      t('differentiators.bilingual'),
      t('differentiators.cardPayment'),
      t('differentiators.businessInvoice'),
      t('differentiators.flexibleCancel'),
    ];
  }
  
  return (
    <section data-testid="differentiators-section" className="py-20 bg-[#0B0B0B] border-t border-[#C9A24D]/10">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#F5F5F5] mb-10 text-center">
            Pourquoi choisir <span className="text-[#C9A24D]">Y GO ?</span>
          </h2>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {differentiators.map((item, index) => (
              <div 
                key={index}
                className="flex items-center gap-3 relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] hover:shadow-[0_22px_55px_rgba(0,0,0,0.85)] transition-all px-5 py-4 backdrop-blur-sm"
              >
                <div className="w-7 h-7 bg-[#C9A24D] flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-[#050509]" />
                </div>
                <span className="text-[#E5E5E5] text-sm leading-relaxed">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Fleet Section - Dynamic with CMS
const FleetSection = ({ cmsContent }) => {
  const { t } = useTranslation();
  
  // Get section data from CMS
  const section = getCMSSection(cmsContent, 'gallery');
  const sectionTitle = section?.title || t('home.ourFleet');
  const sectionSubtitle = section?.subtitle || t('home.fleetSubtitle');
  
  return (
    <section data-testid="fleet-section" className="py-20 bg-[#0B0B0B] border-t border-[#C9A24D]/10">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <div className="text-center mb-12">
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#F5F5F5] mb-4">
            Notre <span className="text-[#C9A24D]">Flotte</span>
          </h2>
          <p className="text-[#A7A7A7] text-base sm:text-lg">
            {sectionSubtitle}
          </p>
        </div>
        
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          {FLEET.map((vehicle, index) => (
            <div 
              key={index}
              className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] hover:shadow-[0_22px_55px_rgba(0,0,0,0.85)] transition-all p-6 text-center backdrop-blur-sm"
            >
              <div className="w-14 h-14 bg-[#C9A24D]/12 flex items-center justify-center mx-auto mb-4">
                <Car className="w-7 h-7 text-[#C9A24D]" />
              </div>
              <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#F5F5F5] mb-1">
                {vehicle.name}
              </h3>
              <p className="text-[#C9A24D] text-sm mb-1">{vehicle.type}</p>
              <p className="text-[#A7A7A7] text-xs">{vehicle.passengers}</p>
            </div>
          ))}
        </div>
        
        {/* Services à bord */}
        <div className="max-w-3xl mx-auto">
          <h3 className="text-center text-[#F5F5F5] font-medium mb-6">{t('fleet.equipment')}</h3>
          <div className="flex flex-wrap justify-center gap-6 text-sm text-[#D4D4D4]">
            <div className="flex items-center gap-2">
              <Wifi className="w-5 h-5 text-[#C9A24D]" />
              <span>Wi‑Fi à bord</span>
            </div>
            <div className="flex items-center gap-2">
              <Droplets className="w-5 h-5 text-[#C9A24D]" />
              <span>Eau minérale</span>
            </div>
            <div className="flex items-center gap-2">
              <Baby className="w-5 h-5 text-[#C9A24D]" />
              <span>Sièges enfants sur demande</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Excursions Section - Dynamic with CMS
const ExcursionsSection = ({ cmsContent }) => {
  const { t } = useTranslation();
  
  // Get section data from CMS - try 'excursions' first, then 'cards' (legacy)
  const section = getCMSSection(cmsContent, 'excursions') || getCMSSection(cmsContent, 'cards');
  const sectionTitle = section?.title || 'Excursions privées depuis Le Havre';
  const sectionSubtitle =
    section?.subtitle ||
    "Découvrez la Normandie au départ du Havre : Étretat, Honfleur, Deauville, plages du Débarquement et plus encore, avec chauffeur privé.";
  
  // Use CMS items if available, otherwise use defaults
  // Map image_url to image for compatibility
  const excursions = section?.items?.length > 0 
    ? section.items.map(item => ({
        ...item,
        image: item.image_url || (DEFAULT_IMAGES[item.id] || DEFAULT_IMAGES.etretat)
      }))
    : DEFAULT_EXCURSIONS;
  
  return (
    <section data-testid="excursions-section" className="py-16 bg-gradient-to-r from-[#111111] to-[#1F1F1F] border-t border-[#C9A24D]/10">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <div className="text-center mb-8">
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#E5E5E5] mb-3">
            Excursions privées depuis <span className="text-[#C9A24D]">Le Havre</span>
          </h2>
          <p className="text-[#E5E5E5]/60 text-sm sm:text-base max-w-3xl mx-auto">
            {sectionSubtitle}
          </p>
        </div>
        
        {/* Carousel horizontal de 6 excursions max */}
        <div className="relative">
          <div className="flex gap-4 overflow-x-auto py-2">
            {excursions.slice(0, 6).map((excursion) => (
              <div
                key={excursion.id}
                data-testid={`excursion-card-${excursion.id}`}
                data-excursion-id={excursion.id}
                className="flex-shrink-0 w-64 relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] hover:shadow-[0_22px_55px_rgba(0,0,0,0.85)] transition-all duration-300 backdrop-blur-sm"
              >
                <div className="relative h-32 overflow-hidden">
                  <SafeImage
                    src={excursion.image}
                    fallbackSrc={DEFAULT_IMAGES.etretat}
                    alt={excursion.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B] to-transparent" />
                  <span className="absolute bottom-2 left-2 text-[#C9A24D] text-xs font-medium">
                    {excursion.duration}
                  </span>
                </div>
                <div className="p-3">
                  <h3 className="font-['Playfair_Display'] text-sm font-semibold text-[#E5E5E5] mb-1 line-clamp-2">
                    {excursion.title}
                  </h3>
                  <p className="text-[#E5E5E5]/70 text-xs leading-relaxed line-clamp-3">
                    {excursion.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="text-center mt-8">
          <Link to="/contact">
            <Button
              data-testid="excursion-cta-quote"
              variant="outline"
              className="border-[#C9A24D] text-[#C9A24D] hover:bg-[#C9A24D]/10 px-8 py-4 text-base rounded-none"
            >
              <Mail className="w-5 h-5 mr-2" />
              {t('common.requestQuote')}
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

// Reviews Section (Google Reviews)
const ReviewsSection = () => {
  // Section désormais gérée par GoogleReviewsSection côté composants
  return null;
};

// Final CTA Section - Dynamic with CMS
const CTASection = ({ cmsContent }) => {
  const { t } = useTranslation();
  
  // Get section data from CMS
  const section = getCMSSection(cmsContent, 'cta');
  const sectionTitle = section?.title || t('home.needDriver');
  const ctaText = section?.cta_text || t('common.bookNow');
  const ctaLink = section?.cta_link || '/booking';
  
  return (
    <section data-testid="cta-section" className="py-20 bg-[#1F1F1F]">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#E5E5E5] mb-8">
            {sectionTitle}
          </h2>
          
          <Link to={ctaLink}>
            <Button 
              data-testid="final-cta-booking"
              className="bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold px-10 py-6 text-lg rounded-none mb-12"
            >
              <Calendar className="w-5 h-5 mr-2" />
              {ctaText}
            </Button>
          </Link>
          
          <div className="grid sm:grid-cols-3 gap-8 text-center">
            <div>
              <Phone className="w-6 h-6 text-[#C9A24D] mx-auto mb-3" />
              <a 
                href="tel:+33278780404" 
                className="text-[#E5E5E5] hover:text-[#C9A24D] font-medium transition-colors"
              >
                +33 2 78 78 04 04
              </a>
            </div>
            <div>
              <Mail className="w-6 h-6 text-[#C9A24D] mx-auto mb-3" />
              <a 
                href="mailto:booking@y-go.fr" 
                className="text-[#E5E5E5] hover:text-[#C9A24D] font-medium transition-colors"
              >
                booking@y-go.fr
              </a>
            </div>
            <div>
              <Clock className="w-6 h-6 text-[#C9A24D] mx-auto mb-3" />
              <p className="text-[#E5E5E5]">{t('common.support')} : 9h–19h</p>
              <p className="text-[#C9A24D] text-sm">{t('common.service247')}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Main HomePage Component - Now with CMS integration
export default function HomePage() {
  // Fetch CMS content for the home page
  const { content: cmsContent, loading: cmsLoading } = useCMSPage('home');
  
  // SEO effect - use CMS meta if available
  useEffect(() => {
    const meta = cmsContent?.meta || {};
    document.title = meta.title || 'Y GO Shuttle Services | VTC Premium Le Havre Normandie';
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = meta.description || 'VTC haut de gamme au Havre : transferts aéroports, excursions Normandie, croisiéristes. Chauffeurs professionnels, véhicules premium. Réservez en ligne.';
    
    document.documentElement.lang = 'fr';
    
    return () => {
      document.title = 'Y GO Shuttle Services';
    };
  }, [cmsContent]);
  
  // Show loading state if CMS is loading (optional, can be removed for instant display with fallbacks)
  if (cmsLoading) {
    return (
      <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#C9A24D] animate-spin" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#0B0B0B]">
      <Header activePage="Accueil" />
      
      <main>
        <HeroSection cmsContent={cmsContent} />
        <ZoneSection />
        <div id="services">
          <ServicesSection cmsContent={cmsContent} />
        </div>
        <DifferentiatorsSection cmsContent={cmsContent} />
        <div id="flotte">
          <FleetSection cmsContent={cmsContent} />
        </div>
        <div id="excursions">
          <ExcursionsSection cmsContent={cmsContent} />
        </div>
        <ElfsightReviewsSection />
        <HomeCtaSection />
      </main>
      
      <Footer />
    </div>
  );
}
